import { CaretDownOutlined } from '@ant-design/icons';
import EditOutlined from '@ant-design/icons/EditOutlined';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import { ESIGN_COLOR, STATUS_COLOR } from '@vindr/services/constants/common.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { formatDateColumn } from '@vindr/utils/dateTime';
import jsxJoin from '@vindr/utils/formatText/jsxJoin';
import cx from '@vindr/utils/helpers/cx';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import { Dropdown, Tag } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import isEqual from 'lodash/isEqual';
import { FC, ReactElement, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportFormStates } from '../ReportForm';
import './SelectOrderHeader.css';

const SelectOrderHeader: FC<iSelectOrderHeader> = ({
  action,
  isCompare,
  disabled,
  disabledAction,
  esignStatus,
  matchedOrders,
  onEditOrder,
  onSelect,
  selectedOrder,
}) => {
  const { t } = useTranslation('Vindoc');
  const { width } = useWindowSize();
  const isMobile = useMemo(() => width < 600, [width]);

  const OrderStatusToPerson = (order: typeof matchedOrders[0]) => {
    if (!order?.id) return <></>;
    const esignStatus = order?.esign_status;

    return (
      <div
        className={cx('select-order-option grid min-h-[24px] items-start gap-4', {
          'grid-cols-[minmax(50px,112px)_minmax(190px,1fr)_minmax(0px,130px)_minmax(50px,112px)_minmax(24px,auto)]':
            !disabled,
          'grid-cols-[minmax(50px,112px)_minmax(190px,1fr)_minmax(0px,130px)_minmax(50px,112px)]':
            disabled,
        })}
      >
        <Tag
          {...(isMobile && { style: { gridArea: 'order_status' } })}
          className="flex justify-center"
          color={STATUS_COLOR[ORDER_STATUS[order?.status]]}
        >
          {(t(ORDER_STATUS[order?.status]) as string).toLocaleUpperCase()}
        </Tag>
        <div
          title={order?.procedure?.name || order?.procedure?.code}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: isMobile ? 3 : 1,
            ...(isMobile && { gridArea: 'procedure_name' }),
          }}
        >
          {order?.procedure?.name || order?.procedure?.code}
        </div>
        <div {...(isMobile && { style: { gridArea: 'update_at' } })}>
          {formatDateColumn(order?.patient_arrived_time)}
        </div>
        {ESIGN_STATUS[esignStatus] ? (
          <Tag
            {...(isMobile && { style: { gridArea: 'esign_status' } })}
            className="flex justify-center"
            color={ESIGN_COLOR[ESIGN_STATUS[esignStatus]]}
          >
            {(t(ESIGN_STATUS[esignStatus]) as string).toLocaleUpperCase()}
          </Tag>
        ) : (
          <span />
        )}
        {disabled ? null : (
          <EditOutlined
            className="cursor-pointer justify-self-end"
            {...(isMobile && { style: { gridArea: 'edit_button' } })}
            onClick={(e) => {
              e.preventDefault();
              // e.stopPropagation();
              onEditOrder?.(order);
            }}
          />
        )}
      </div>
    );
  };

  const orderMenuItems: ItemType[] = matchedOrders?.map((order) => ({
    key: order?.id,
    label: OrderStatusToPerson(order),
    onClick: () => onSelect?.(order),
  }));

  const orderTitle = useMemo(() => {
    const arr = [
      t(ORDER_STATUS[selectedOrder?.status]),
      selectedOrder?.procedure?.name || '',
      formatDateColumn(selectedOrder?.updated_at),
      t(ESIGN_STATUS[esignStatus]),
    ];
    const filted = arr.filter(Boolean).map((x, index) => {
      if (index === 1) {
        return (
          <span
            key={x}
            className="whitespace-normal"
            title={x}
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
            }}
          >
            {x}
          </span>
        );
      }
      return <span key={x}>{x}</span>;
    });

    return jsxJoin(filted, <span> | </span>);
  }, [
    esignStatus,
    selectedOrder?.procedure?.name,
    selectedOrder?.status,
    selectedOrder?.updated_at,
    t,
  ]);

  return (
    <div className="relative flex w-full items-center justify-between rounded-t-sm bg-background-dark-7">
      <Dropdown
        placement="bottomLeft"
        trigger={['click']}
        menu={{
          items: orderMenuItems,
          selectable: true,
          selectedKeys: [selectedOrder?.id],
        }}
        overlayClassName={cx('w-screen min-[600px]:min-w-[600px] max-h-screen overflow-auto', {
          'min-[600px]:w-[37.5vw]': isCompare,
          'min-[600px]:w-[77vw]': !isCompare,
        })}
      >
        <div className="flex w-full py-1 px-2 pr-2">
          <CaretDownOutlined className="mr-2 cursor-pointer" />
          <span className="inline-flex w-full flex-1 gap-[4px] overflow-hidden whitespace-nowrap">
            {orderTitle}
          </span>
        </div>
      </Dropdown>
      {!disabledAction && action}
    </div>
  );
};

const MemoSelectOrderHeader = memo(
  SelectOrderHeader,
  (prev, next) =>
    isEqual(prev.matchedOrders, next.matchedOrders) &&
    isEqual(prev.selectedOrder, next.selectedOrder) &&
    prev.esignStatus === next.esignStatus &&
    prev.isCompare === next.isCompare &&
    prev.disabled === next.disabled &&
    prev.disabledAction === next.disabledAction &&
    prev.reportVersions === next.reportVersions &&
    prev.selectedReportVersion === next.selectedReportVersion
);
MemoSelectOrderHeader.displayName = 'SelectOrderHeader';

export default MemoSelectOrderHeader;

interface SelectOrderHeaderCommon {
  action: ReactElement;
  esignStatus: ReportFormStates['esignStatus'];
  matchedOrders: ReportFormStates['matchedOrders'];
  selectedOrder: ReportFormStates['selectedOrder'];
  reportVersions: any;
  selectedReportVersion: any;
  isCompare?: boolean;
}

type SelectOrderHeaderDisabled = SelectOrderHeaderCommon & {
  disabled: boolean;
  disabledAction: boolean;
  onEditOrder?: (order: ReportFormStates['matchedOrders'][0]) => void;
  onSelect?: (order: ReportFormStates['matchedOrders'][0]) => void;
};

type iSelectOrderHeader = SelectOrderHeaderDisabled & Partial<SelectOrderHeaderDisabled>;
